import store from 'store';
import is from '@/locale/flatpickr/is';
import auth from '@/api/auth';
/**
 * Vinna úr blaðsíðupplýsingum frá vefþjón.
 *
 * @param {objects} response Svar frá vefþjón.
 */
export const parsePagination = (pagination, data) => {
  if (Array.isArray(pagination)) {
    // eslint-disable-next-line no-param-reassign
    [pagination] = pagination;
  }
  // eslint-disable-line import/prefer-default-export, max-len
  const currentLimit = parseInt(pagination['x-current-limit'], 10);
  const currentPage = parseInt(pagination['x-current-page'], 10);
  const totalCount = parseInt(pagination['x-total-count'], 10);
  const totalPages = parseInt(pagination['x-total-pages'], 10);

  const showingFrom = currentLimit * (currentPage - 1) + 1;
  const showingTo = currentLimit * (currentPage - 1) + data.length;

  return {
    currentLimit,
    currentPage,
    totalCount,
    totalPages,
    showingFrom,
    showingTo,
  };
};

/*
 * Sjálfgefinn fjöldi leitarniðurstaðna á blaðsíðu (samsvarar sjálfgefnu vefþjóns).
 */
export const DEFAULT_PAGE_LIMIT = 10;

const pageLimitStorageKey = (key) => `fbk.pageLimit.${key}`;

/*
 * Sækja vistað limit-val notanda úr localStorage.
 * Skilar fallback ef ekkert er vistað eða gildið er ógilt.
 */
export const getStoredLimit = (key, fallback = DEFAULT_PAGE_LIMIT) => {
  const value = parseInt(store.get(pageLimitStorageKey(key)), 10);
  return Number.isInteger(value) && value > 0 ? value : fallback;
};

/*
 * Vista limit-val notanda í localStorage.
 */
export const setStoredLimit = (key, limit) => {
  store.set(pageLimitStorageKey(key), limit);
};

/*
 * Mixin fyrir blaðsíður og limit.
 *
 * Til að gera limit-val varanlegt (localStorage): skilgreindu `paginationStorageKey`
 * á component-inu og láttu `loadList(page = null, limit = this.limit)` sækja
 * sjálfgefna gildið úr `this.limit`. Sé `paginationStorageKey` ekki skilgreint
 * hegðar mixin-ið sér óbreytt (this.limit = null).
 */
export const paginationMixin = {
  data() {
    const key = this.$options.paginationStorageKey;
    return {
      limit: key ? getStoredLimit(key) : null,
    };
  },
  methods: {
    /*
     * Breyta um blaðsíðu. Heldur núverandi limit-vali.
     */
    changePage(page) {
      this.loadList(page, this.limit != null ? this.limit : this.pagination.currentLimit);
    },
    /*
     * Breyta leitarniðurstöðum á blaðsíðu. Vistar valið sé storageKey skilgreint.
     */
    changeLimit(limit) {
      this.limit = limit;
      if (this.$options.paginationStorageKey) {
        setStoredLimit(this.$options.paginationStorageKey, limit);
      }
      this.loadList(null, limit);
    },
  },
};

/*
 * Villuskilaboð frá Django yfir í VeeValidate.
 */
export const djangoErrorsToVee = (data, errorBag, errors) => {
  if (Array.isArray(data)) {
    data.forEach((e) => errors.push(e));
  } else {
    const { non_field_errors: nonFieldErrors, ...other } = data;

    if (nonFieldErrors) {
      nonFieldErrors.forEach((err) => {
        errors.push(err);
      });
    }

    Object.entries(other).forEach(([k, v]) => {
      v.forEach((e) => errorBag.add(k, e, 'server'));
    });
  }
};

export const flatpickrConfig = () => ({
  wrap: true,
  dateFormat: 'Y-m-d',
  locale: is,
  allowInput: false,
  altInput: true,
  altFormat: 'd.m.Y',
});

export const fileDropzoneConfig = () => ({
  url: '/',
  maxFilesize: 500,
  maxFiles: 10,
  paramName: 'file',
  acceptedFiles: '.docx,.doc,.pdf,.xlsx,.xls',
  autoProcessQueue: true,
  addRemoveLinks: true,
  dictDefaultMessage: '<i class="fa fa-cloud-upload"></i> Smelltu hér eða dragðu skrá inn á svæðið.',
  dictFileTooBig: 'Skrá er of stór ({{filesize}} MB). Hámarksstærð er {{maxFilesize}} MB.',
  dictInvalidFileType: 'Skrá er ekki á leyfilegu skráarsniði.',
  dictRemoveFile: 'Fjarlægja skrá.',
  dictMaxFilesExceeded: 'Hámarksfjölda skráa náð.',
  headers: {
    token: auth.getToken(),
  },
});

export const imageDropzoneConfig = () => ({
  url: '/',
  maxFilesize: 5, // mb
  maxFiles: 5,
  paramName: 'image',
  acceptedFiles: '.jpg,.jpeg,.png',
  // createImageThumbnails: true,
  // thumbnailWidth: 250, // px
  autoProcessQueue: true,
  addRemoveLinks: true,
  // forceFallback: true,
  dictDefaultMessage: '<i class="fa fa-cloud-upload"></i> Smelltu hér eða dragðu skrá inn á svæðið.',
  dictFileTooBig: 'Skrá er of stór ({{filesize}} MB). Hámarksstærð er {{maxFilesize}} MB.',
  dictInvalidFileType: 'Skrá er ekki á leyfilegu skráarsniði.',
  dictRemoveFile: 'Fjarlægja skrá.',
  dictMaxFilesExceeded: 'Hámarksfjölda skráa náð.',
  headers: {
    token: auth.getToken(),
  },
});

export function debounce(fn, wait) {
  let timer;
  return function (...args) {
    if (timer) {
      clearTimeout(timer); // clear any pre-existing timer
    }
    const context = this; // get the current context
    timer = setTimeout(() => {
      fn.apply(context, args); // call the function if time expires
    }, wait);
  };
}
